import { Query } from "@apollo/client/react/components";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import type { NextPage } from "next";
import OverdueSubscriptionStickyBanner from "../../components/billing/OverdueSubscriptionStickyBanner";
import AmericanCompany from "../../components/company/AmericanCompany";
import AmericanCompanyContext from "../../components/company/AmericanCompanyContext";
import GatedAmericanCompanyCard from "../../components/company/GatedAmericanCompanyCard";
import NotFound from "../../components/errors/NotFound";
import LocaleContext from "../../components/i18n/LocaleContext";
import FundamenteiWithTextLayout from "../../components/layouts/FundamenteiWithTextLayout";
import NavbarLayout from "../../components/layouts/NavbarLayout";
import useSelfie from "../../lib/hooks/useSelfie";
import SetHttpStatusCode from "../../lib/http/SetHttpStatusCode";
import { ISecurityCountry } from "../../types/graphqlTypes";

interface IAssetByTickerSymbolProps {
  tickerSymbol: string | string[] | undefined;
}

const AssetByTickerSymbol: NextPage<IAssetByTickerSymbolProps> = function AssetByTickerSymbol({ tickerSymbol }) {
  const user = useSelfie();

  if (!tickerSymbol || tickerSymbol.length > 6) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (/^([0-9]+)$/.test(Array.isArray(tickerSymbol) ? tickerSymbol[0] : tickerSymbol)) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  const normalizedTickerSymbol = Array.isArray(tickerSymbol)
    ? tickerSymbol[0].toUpperCase()
    : tickerSymbol.toUpperCase();

  return (
    <Query<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>
      query={IssuerByTickerSymbolQuery}
      variables={{
        tickerSymbol: normalizedTickerSymbol,
        country: ISecurityCountry.Us,
      }}
    >
      {({ data, loading }) => {
        if (loading) {
          return null;
        }

        if (!data) {
          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }

        if (!data.issuerByTickerSymbol) {
          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }

        if (data.issuerByTickerSymbol.__typename === "AmericanCompanyIssuer") {
          if (user === null) {
            return (
              <FundamenteiWithTextLayout>
                <LocaleContext.Provider value="USA">
                  <AmericanCompanyContext.Provider value={data.issuerByTickerSymbol}>
                    <GatedAmericanCompanyCard
                      company={data.issuerByTickerSymbol}
                      tickerSymbol={normalizedTickerSymbol}
                    />
                  </AmericanCompanyContext.Provider>
                </LocaleContext.Provider>
              </FundamenteiWithTextLayout>
            );
          }

          return (
            <NavbarLayout>
              <OverdueSubscriptionStickyBanner />

              <LocaleContext.Provider value="USA">
                <AmericanCompanyContext.Provider value={data.issuerByTickerSymbol}>
                  <AmericanCompany company={data.issuerByTickerSymbol} tickerSymbol={normalizedTickerSymbol} />
                </AmericanCompanyContext.Provider>
              </LocaleContext.Provider>
            </NavbarLayout>
          );
        }

        return (
          <>
            <SetHttpStatusCode httpStatus={404} />
            <NotFound />
          </>
        );
      }}
    </Query>
  );
};

AssetByTickerSymbol.getInitialProps = async ({ query }) => ({
  tickerSymbol: Array.isArray(query.tickerSymbol) ? query.tickerSymbol[0] : query.tickerSymbol,
});

export default AssetByTickerSymbol;
